import {
  Vector3,
  BufferGeometry,
  BufferAttribute,
  Color,
  ShaderMaterial,
  Points,
} from 'three';

import {
  TweenLite,
  Power2,
  Bounce,
  // Elastic,
  Power3,
  Expo,
} from 'gsap';

import queryString from 'query-string';
import TextToImageConverter from '../../lib/TextToImageConverter';

const queryParams = queryString.parse(window.location.search);


const getParticleGridSize = () => window.innerWidth / (queryParams.particlesPerWindow || 4000);
const getMaxDistance = () => getParticleGridSize() * 15 * (queryParams.maxDistanceMultiplier || 1);

const PARTICLE_SIZE = queryParams.particleSize || 4;
const MAX_DOTS = 100000;
const SENSITIVITY_MODIFIER = queryParams.sensitivity || 1;

export default {
  name: 'CircleLayout',
  inject: ['scene'],
  props: ['message', 'mousePos', 'fontSize', 'fontFamily', 'fontWeight', 'color'],
  watch: {
    mousePos(newVal) {
      TweenLite.killTweensOf(this.material.uniforms.mousePos.value);
      TweenLite.to(this.material.uniforms.mousePos.value, 1, {
        ...newVal,
        ease: Expo.easeOut, // Elastic.easeOut,
        onComplete: () => {
          TweenLite.to(this.material.uniforms.mousePos.value, 0.75, {
            z: -getMaxDistance(),
            ease: Bounce.easeOut,
            delay: 1,
          });
        },
      });
    },
    message() {
      this.applyChanges();
    },
    fontSize() {
      this.applyChanges();
    },
    fontWeight() {
      this.applyChanges();
    },
    fontFamily() {
      this.applyChanges();
    },
  },
  destroyed() {
    this.scene.remove(this.particles);
  },
  async mounted() {
    this.converter = new TextToImageConverter();
    const geometry = this.createGeometry();
    this.applyChanges();
    this.material = new ShaderMaterial({
      uniforms: {
        color: { value: new Color(this.color) },
        mousePos: { value: new Vector3(0, 0, -getMaxDistance()), type: 'v3' },
        maxDist: { value: getMaxDistance(), type: 'f' },
      },
      vertexShader: this.$refs.vertexshader.textContent,
      fragmentShader: this.$refs.fragmentshader.textContent,
      transparent: true,
    });

    this.particles = new Points(geometry, this.material);
    this.scene.add(this.particles);
  },
  methods: {
    explode() {
      TweenLite.killTweensOf(this.material.uniforms.maxDist);
      TweenLite.to(this.material.uniforms.maxDist, 0.5, {
        value: getMaxDistance() * 2 + this.material.uniforms.maxDist.value,
        ease: Power2.easeInOut,
        onComplete: () => {
          TweenLite.to(this.material.uniforms.maxDist, 0.5, {
            value: getMaxDistance(),
            ease: Power3.easeOut,
            delay: 1,
          });
        },
      });
    },
    animateIn(geometry, vertices, time, delay) {
      const counter = { var: 0 };

      geometry.attributes.position.needsUpdate = true;
      geometry.setDrawRange(0, 0);

      TweenLite.to(counter, time || 2, {
        var: vertices.length,
        delay: delay || 1,
        onUpdate() {
          Math.round(counter.var);
          if (geometry.drawRange.count !== Math.round(counter.var)) {
            geometry.attributes.position.needsUpdate = true;
            geometry.setDrawRange(0, Math.round(counter.var));
          }
        },
        ease: Power2.easeInOut,
      });
    },
    getVertices(imageData, width, height) {
      if (!this.vertices) this.vertices = [];
      const vertices = this.vertices;
      let counter = 0;
      for (let i = 0; i < imageData.length; i += 4) {
        const alpha = imageData[i + 3];

        const pixelIndex = i / 4;
        const x = (pixelIndex % width) - width / 2;
        const y = (pixelIndex - x) / width - height / 2;

        if (alpha > 0) {
          if (!vertices[counter]) {
            vertices[counter] = new Vector3(
              x * getParticleGridSize(),
              -y * getParticleGridSize(),
              0,
            );
          } else {
            vertices[counter].set(
              x * getParticleGridSize(),
              -y * getParticleGridSize(),
              0,
            );
          }

          counter++;
        }
      }
      vertices.length = counter;
      return vertices;
    },
    applyVertices(vertices) {
      const color = new Color();
      for (let i = 0, l = vertices.length; i < l; i++) {
        const vertex = vertices[i];
        vertex.toArray(this.positions, i * 3);
        color.setHSL(0.5 * (i / l), 1.0, 0.5);
        color.toArray(this.colors, i * 3);
        this.sizes[i] = PARTICLE_SIZE;
        this.sensitivity[i] =
          Math.random() * SENSITIVITY_MODIFIER + 0.5 * SENSITIVITY_MODIFIER;
      }

      this.geometry.attributes.position.needsUpdate = true;
      this.geometry.attributes.customColor.needsUpdate = true;
      this.geometry.attributes.size.needsUpdate = true;
      this.geometry.attributes.sensitivity.needsUpdate = true;

      this.geometry.setDrawRange(0, vertices.length);
      this.animateIn(this.geometry, this.vertices, 0.15, 0.001);
    },
    animateApplyVertices(vertices) {
      const counter = { var: this.geometry.drawRange.count };

      TweenLite.to(counter, 0.15, {
        var: 0,
        roundProps: 'var',
        onUpdate: () => {
          if (this.geometry.drawRange.count !== counter.var) {
            this.geometry.attributes.position.needsUpdate = true;
            this.geometry.setDrawRange(0, counter.var);
          }
        },
        ease: Power2.easeInOut,
        onComplete: () => this.applyVertices(vertices),
      });
    },
    createGeometry() {
      this.positions = new Float32Array(MAX_DOTS * 3);
      this.colors = new Float32Array(MAX_DOTS * 3);
      this.sizes = new Float32Array(MAX_DOTS);
      this.sensitivity = new Float32Array(MAX_DOTS);

      this.geometry = new BufferGeometry();
      this.geometry.setAttribute(
        'position',
        new BufferAttribute(this.positions, 3),
      );
      this.geometry.setAttribute(
        'customColor',
        new BufferAttribute(this.colors, 3),
      );
      this.geometry.setAttribute('size', new BufferAttribute(this.sizes, 1));
      this.geometry.setAttribute(
        'sensitivity',
        new BufferAttribute(this.sensitivity, 1),
      );

      this.geometry.setDrawRange(0, 0);

      return this.geometry;
    },
    async applyChanges() {
      const fontFamily = this.fontFamily || 'Arial';
      const fontSize = this.fontSize || 42;

      await document.fonts.load(`${this.fontSize}px ${this.fontFamily}`, this.message);

      const data = this.converter.getImageData(
        this.message,
        fontFamily,
        fontSize,
        this.fontWeight || 800,
      );

      const vertices = this.getVertices(
        data,
        this.converter.width,
        this.converter.height,
      ).sort((vertexA, vertexB) => {
        const a = vertexA.length();
        const b = vertexB.length();

        return a > b ? 1 : a < b ? -1 : 0;
      });

      this.animateApplyVertices(vertices);
    },
  },
};



// WEBPACK FOOTER //
// ./src/components/CircleLayout/script.js